import { Card, CardContent } from "@repo/ui";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { CheckCircle, AlertTriangle, XCircle, Package } from "lucide-react";
import type { InventoryStatus } from "@/hooks/use-analytics-data";

interface InventoryStatusOverviewProps {
  status: InventoryStatus;
  totalItems: number;
}

const STATUS_CONFIG = [
  {
    key: "in_stock" as const,
    label: "In Stock",
    color: "#059669",
    bgColor: "bg-muted/60",
    textColor: "text-muted-foreground",
    Icon: CheckCircle,
  },
  {
    key: "reorder_needed" as const,
    label: "Reorder Needed",
    color: "#d97706",
    bgColor: "bg-muted/60",
    textColor: "text-muted-foreground",
    Icon: AlertTriangle,
  },
  {
    key: "sold_out" as const,
    label: "Sold Out",
    color: "#dc2626",
    bgColor: "bg-muted/60",
    textColor: "text-muted-foreground",
    Icon: XCircle,
  },
];

function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { fill: string } }>;
}) {
  if (!active || !payload?.[0]) return null;

  const entry = payload[0];

  return (
    <div className="rounded-lg border border-border bg-surface p-2.5 shadow-lg">
      <div className="flex items-center gap-2">
        <div
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: entry.payload.fill }}
        />
        <span className="text-xs text-muted-foreground">{entry.name}</span>
        <span className="text-xs font-semibold tabular-nums">
          {entry.value}
        </span>
      </div>
    </div>
  );
}

export function InventoryStatusOverview({
  status,
  totalItems,
}: InventoryStatusOverviewProps) {
  const pieData = STATUS_CONFIG.map((config) => ({
    name: config.label,
    value: status[config.key],
    fill: config.color,
  }));

  const hasItems = totalItems > 0;

  return (
    <div className="grid gap-4 md:grid-cols-4">
      {/* Status Cards */}
      {STATUS_CONFIG.map((config) => {
        const count = status[config.key];
        const percentage =
          totalItems > 0 ? Math.round((count / totalItems) * 100) : 0;

        return (
          <Card key={config.key}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    {config.label}
                  </p>
                  <p className="text-2xl font-semibold tracking-tight mt-1 tabular-nums">
                    {count}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {percentage}% of inventory
                  </p>
                </div>
                <div className={`rounded-lg ${config.bgColor} p-2.5`}>
                  <config.Icon className={`h-5 w-5 ${config.textColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Donut Chart */}
      <Card>
        <CardContent className="p-5 flex flex-col items-center justify-center">
          {!hasItems ? (
            <div className="flex flex-col items-center text-center">
              <div className="mb-2 rounded-full bg-muted p-2.5">
                <Package className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground">No drugs</p>
            </div>
          ) : (
            <div className="relative">
              <ResponsiveContainer width={120} height={120}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={55}
                    paddingAngle={2}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-lg font-semibold tabular-nums leading-none">
                    {totalItems}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    total
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
